import { ChangeEvent, forwardRef, useRef, useState, useId } from 'react';
import classNames from 'classnames';
import { AssistiveText } from '@components/input/AssistiveText';
import { Text } from '@components/text';
import './Switch.scss';

type OnChangeFunctionType = (event: ChangeEvent) => void;
type OnFocusFunctionType = (event: React.FocusEvent) => void;
type OnBlurFunctionType = (event: React.FocusEvent) => void;

export interface SwitchProps {
  id?: string;
  defaultChecked?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  onBlur?: OnBlurFunctionType;
  onChange?: OnChangeFunctionType;
  onFocus?: OnFocusFunctionType;
  onLabel?: string;
  offLabel?: string;
  className?: string;
  /** If true, applies error styling and sets aria-invalid on the input. */
  error?: boolean;
  /** Error or hint text rendered below the switch. When present, wired via
   *  aria-describedby so screen readers announce it on focus. */
  assistiveText?: string;
}

/**
 * Switch Base component, an `<input type="checkbox" />` element is underneath to support accessibility
 * @param id - use to connect label to input element for screen reader
 * @param defaultChecked
 * @param disabled
 * @param hideLabel
 * @param onBlur
 * @param onChange
 * @param onFocus
 * @param onLabel
 * @param offLabel
 * @param className
 * @param error - If true, applies error styling and sets aria-invalid on the input.
 * @param assistiveText - Error or hint text rendered below the switch.
 * @constructor
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      id,
      onLabel,
      offLabel,
      hideLabel,
      defaultChecked,
      disabled,
      onChange,
      onFocus,
      onBlur,
      className,
      error,
      assistiveText,
      ...rest
    },
    ref,
  ) => {
    const generatedId = useId();
    const resolvedId = id ?? generatedId;
    const checkboxRef = useRef<HTMLInputElement | null>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [localChecked, setLocalChecked] = useState(defaultChecked ?? false);

    const handleFocus = (e: React.FocusEvent) => {
      setIsFocused(true);
      if (onFocus) onFocus(e);
    };

    const handleBlur = (e: React.FocusEvent) => {
      setIsFocused(false);
      if (onBlur) onBlur(e);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setLocalChecked((prevState: boolean) => !prevState);
      if (onChange) onChange(e);
    };

    const handleClick = () => {
      if (disabled) return;
      checkboxRef.current?.click();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();
        checkboxRef.current?.click();
      }
    };

    const initRefs = (element: HTMLInputElement) => {
      checkboxRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    const assistiveTextId = assistiveText ? `${resolvedId}-text` : undefined;

    return (
      <div className={classNames('vds-switch', { 'vds-switch--error': error }, className)}>
        {!hideLabel && (
          <Text
            className={classNames('vds-switch__label', { 'vds-switch__label--disabled': disabled })}
            variant="body-02">
            <label htmlFor={resolvedId}>{localChecked ? onLabel || 'On' : offLabel || 'Off'}</label>
          </Text>
        )}
        <span
          className={classNames('vds-switch__root', {
            'vds-switch__root--disabled': disabled,
            'vds-switch__root--checked': localChecked,
            'vds-switch__root--focus': isFocused,
          })}
          onClick={handleClick}>
          <span className="vds-switch__thumb"></span>
          <span className="vds-switch__track"></span>
        </span>
        <input
          id={resolvedId}
          defaultChecked={defaultChecked}
          disabled={disabled}
          type="checkbox"
          ref={initRefs}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          aria-invalid={error || undefined}
          aria-describedby={assistiveTextId}
          {...rest}
        />
        {assistiveText ? (
          <AssistiveText error={error} disabled={disabled} id={assistiveTextId}>
            {assistiveText}
          </AssistiveText>
        ) : null}
      </div>
    );
  },
);
